var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { DatePicker as AntdDatePicker } from 'antd';
import Button from '../Button';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import { DATE_FORMAT, DATE_TIME_FORMAT, TIME_FORMAT } from './consts';
import useBooleanState from '../../hooks/useBooleanState';
import cls from 'classnames';
import TextInput from '../TextInput';
import { BaseInputWithIcons } from '../BaseInput/BaseInput';
import ListItem from '../ListItem';
import classNames from 'classnames';
import Icon from '../Icon';
import { generateRangePresets } from './DatePicker.utils';
// Fix for `clone.weekday is not a function` exception
// https://github.com/ant-design/ant-design/issues/26190#issuecomment-703673400
dayjs.extend(weekday);
dayjs.extend(localeData);
var STYLE_NAMESPACE = 'vcp-date-picker';
var LIST_ITEM_STYLE_NAMESPACE = 'vcp-list-item';
var RANGE_PICKER_MODE = ['date', 'date'];
var DatePicker = function (_a) {
    var variant = _a.variant, onChange = _a.onChange, isRange = _a.isRange, withTime = _a.withTime, defaultValue = _a.defaultValue, _b = _a.placeholder, placeholder = _b === void 0 ? isRange ? ['Start date', 'End date'] : DATE_FORMAT : _b, disabled = _a.disabled, minDate = _a.minDate, maxDate = _a.maxDate, allowClear = _a.allowClear, _c = _a.applyProps, applyProps = _c === void 0 ? { title: 'Apply' } : _c, _d = _a.cancelProps, cancelProps = _d === void 0 ? { title: 'Cancel' } : _d, _e = _a.clearProps, clearProps = _e === void 0 ? { title: 'Clear' } : _e;
    var isInitialized = React.useRef(false);
    var dateFormat = withTime ? DATE_TIME_FORMAT : DATE_FORMAT;
    var emptyValue = React.useMemo(function () {
        return isRange ? [null, null] : null;
    }, [isRange]);
    var defaultDate = React.useMemo(function () {
        if (isRange) {
            if (!Array.isArray(defaultValue)) {
                return emptyValue;
            }
            var defaultStartDate = defaultValue[0], defaultEndDate = defaultValue[1];
            return [
                defaultStartDate ? dayjs(defaultStartDate) : null,
                defaultEndDate ? dayjs(defaultEndDate) : null,
            ];
        }
        else {
            return defaultValue ? dayjs(defaultValue) : emptyValue;
        }
    }, [isRange, defaultValue]);
    var rangePickerFormat = React.useMemo(function () { return ({
        format: dateFormat,
    }); }, [dateFormat]);
    var _f = useBooleanState(false), isOpen = _f[0], handleOpen = _f[2], handleClose = _f[3];
    var _g = React.useState(defaultDate), tempDate = _g[0], setTempDate = _g[1];
    var _h = React.useState(defaultDate), selectedDate = _h[0], setSelectedDate = _h[1];
    var rangePresets = React.useMemo(function () {
        return generateRangePresets(minDate).map(function (_a) {
            var _b;
            var _c, _d;
            var label = _a.label, value = _a.value;
            var isSelected = tempDate &&
                Array.isArray(tempDate) &&
                ((_c = value[0]) === null || _c === void 0 ? void 0 : _c.isSame(tempDate[0])) &&
                ((_d = value[1]) === null || _d === void 0 ? void 0 : _d.isSame(tempDate[1]));
            return {
                label: (React.createElement(ListItem, { title: label, color: isSelected ? 'primary' : 'default', className: classNames((_b = {},
                        _b["".concat(LIST_ITEM_STYLE_NAMESPACE, "__selected")] = true,
                        _b)), rightComponent: isSelected ? (React.createElement(Icon, { name: "checkmark", className: "".concat(LIST_ITEM_STYLE_NAMESPACE, "__selected") })) : undefined })),
                value: value,
            };
        });
    }, [tempDate]);
    React.useEffect(function () {
        var _a, _b, _c;
        if (!isInitialized.current)
            return;
        if (isRange) {
            var _d = selectedDate, start = _d[0], end = _d[1];
            onChange([
                (_a = start === null || start === void 0 ? void 0 : start.format(dateFormat)) !== null && _a !== void 0 ? _a : '',
                (_b = end === null || end === void 0 ? void 0 : end.format(dateFormat)) !== null && _b !== void 0 ? _b : '',
            ]);
        }
        else {
            var date = selectedDate;
            onChange((_c = date === null || date === void 0 ? void 0 : date.format(dateFormat)) !== null && _c !== void 0 ? _c : '');
        }
    }, [selectedDate]);
    React.useEffect(function () {
        isInitialized.current = true;
    }, []);
    var disableDate = React.useCallback(function (current) {
        if (maxDate && current && current > dayjs(maxDate, dateFormat)) {
            return true;
        }
        if (minDate && current && current < dayjs(minDate, dateFormat)) {
            return true;
        }
        return false;
    }, []);
    var handleClear = React.useCallback(function () {
        setSelectedDate(emptyValue);
        setTempDate(emptyValue);
        handleClose();
    }, []);
    var handleDatePick = React.useCallback(function () {
        setSelectedDate(tempDate);
        handleClose();
    }, [tempDate, dateFormat]);
    var handlePickerValueChange = React.useCallback(function (date, info) {
        if (tempDate === null && selectedDate === null && info.source === 'reset')
            return;
        if (!withTime && isRange && date && Array.isArray(date))
            setTempDate([
                date[0] ? date[0].startOf('d') : null,
                date[1] ? date[1].endOf('d') : null,
            ]);
        else
            setTempDate(date);
    }, []);
    var handleCancel = React.useCallback(function () {
        setTempDate(emptyValue);
        handleClose();
    }, []);
    var renderExtraFooter = React.useCallback(function () { return (React.createElement("div", { className: "".concat(STYLE_NAMESPACE, "-action-bar") },
        selectedDate ? (React.createElement(Button, { buttonType: "destructive", size: "small", onClick: handleClear }, clearProps.title)) : (React.createElement("div", null)),
        React.createElement("div", { className: "".concat(STYLE_NAMESPACE, "-action-bar-main") },
            React.createElement(Button, { buttonType: "secondary", buttonStyle: "ghost", size: "small", onClick: handleCancel }, cancelProps.title),
            React.createElement(Button, { size: "small", onClick: handleDatePick }, applyProps.title)))); }, [tempDate, selectedDate]);
    var shouldShowTime = React.useMemo(function () { return (withTime ? { format: TIME_FORMAT } : false); }, []);
    var components = React.useMemo(function () {
        return {
            input: React.forwardRef(function InnerWrapper(props, ref) {
                return (React.createElement(TextInput, __assign({}, props, { type: variant, inputRef: ref, leadingIcon: selectedDate && allowClear
                        ? {
                            iconName: 'close',
                            onClick: handleClear,
                        }
                        : { iconName: 'calendar', onClick: handleOpen } })));
            }),
        };
    }, [variant, selectedDate]);
    var pickerProps = {
        needConfirm: false,
        showTime: shouldShowTime,
        disabled: disabled,
        inputReadOnly: true,
        disabledDate: disableDate,
        className: cls(STYLE_NAMESPACE, {
            disabled: disabled,
            opened: isOpen,
            filled: selectedDate,
        }),
        popupClassName: "".concat(STYLE_NAMESPACE, "-popup"),
        allowClear: false,
        open: isOpen,
        onClick: handleOpen,
        showNow: false,
        renderExtraFooter: renderExtraFooter,
        value: selectedDate,
        suffixIcon: false,
    };
    return isRange ? (React.createElement(BaseInputWithIcons, { type: variant, leadingIcon: selectedDate && allowClear
            ? {
                iconName: 'close',
                onClick: handleClear,
            }
            : { iconName: 'calendar', onClick: handleOpen } }, function () { return (React.createElement(AntdDatePicker.RangePicker, __assign({ mode: RANGE_PICKER_MODE }, pickerProps, { onCalendarChange: handlePickerValueChange, placeholder: placeholder, value: selectedDate, presets: rangePresets, format: rangePickerFormat }))); })) : (React.createElement(AntdDatePicker, __assign({}, pickerProps, { format: dateFormat, onFocus: handleOpen, onPickerValueChange: handlePickerValueChange, placeholder: placeholder, value: selectedDate, components: components })));
};
export default React.memo(DatePicker);
